import { inject, Injectable } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import Europa from 'europa';
import { Plugin, PluginApi, PluginConverter } from 'europa-core';
import { difference, uniq } from 'lodash-es';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { TagsFormComponent } from '../form/tags/tags.component';
import { Ext } from '../model/ext';
import { Store } from '../store/store';
import { getMailboxes } from '../util/editor';
import { getPath } from '../util/http';
import { access, removePrefix, setPublic } from '../util/tag';
import { AdminService } from './admin.service';
import { ExtService } from './api/ext.service';
import { ConfigService } from './config.service';

export type TagPreview = { name?: string,  bookmark?: string, tag: string } | Ext;

@Injectable({
  providedIn: 'root'
})
export class EditorService {
  private config = inject(ConfigService);
  private admin = inject(AdminService);
  private exts = inject(ExtService);
  private store = inject(Store);


  constructor() {
    const superscriptProvider = (api: PluginApi): Plugin => ({
      converters: {
        SUP: {
          startTag(conversion): boolean {
            conversion.output('^');
            conversion.atNoWhitespace = true;
            return true;
          },
        },
      },
    });
    const paragraphConverter: PluginConverter = {
      startTag(conversion, context): boolean {
        // @ts-ignore
        if (conversion.atParagraph || conversion.inline) return true;
        conversion.appendParagraph();
        return true;
      },

      endTag(conversion, context) {
        // @ts-ignore
        if (conversion.inline) return;
        conversion.appendParagraph();
      },
    };
    const paragraphProvider = (api: PluginApi): Plugin => ({
      converters: {
        ADDRESS: paragraphConverter,
        ARTICLE: paragraphConverter,
        ASIDE: paragraphConverter,
        DIV: paragraphConverter,
        FIELDSET: paragraphConverter,
        FOOTER: paragraphConverter,
        HEADER: paragraphConverter,
        MAIN: paragraphConverter,
        NAV: paragraphConverter,
        P: paragraphConverter,
        SECTION: paragraphConverter,
      },
    });
    const paragraphTags = new Set(['ADDRESS', 'ARTICLE', 'ASIDE', 'DIV', 'FIELDSET', 'FOOTER', 'HEADER', 'MAIN', 'NAV', 'P', 'SECTION']);
    const blockTags = new Set(['BLOCKQUOTE', 'BR', 'DD', 'DETAILS', 'DT', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'HR', 'LI', 'PRE']);
    const emphasisTags = new Set(['B', 'STRONG', 'CITE', 'DFN', 'EM', 'I', 'U', 'VAR']);
    const delimiterTags = new Set(['CODE', 'KBD', 'Q', 'SAMP', 'SUP']);
    const emitsBlock = (el: Element, tag: string) =>
      blockTags.has(tag) || paragraphTags.has(tag) && !el.parentElement?.closest('a[href]:not([href=""])');
    const mediaSource = (el: Element, tag: string) => tag === 'IMG'
      ? el.getAttribute('src')
      : el.querySelector('source')?.getAttribute('src') || el.getAttribute('src');
    const firstMarkdownChar = (node: Node, skip: Set<string>): string => {
      if (node.nodeType === Node.TEXT_NODE) return (node.textContent || '').match(/^./su)?.[0] || '';
      if (node.nodeType !== Node.ELEMENT_NODE) return '';
      const el = node as HTMLElement;
      const tag = el.tagName.toUpperCase();
      if (skip.has(tag) || el.style?.display === 'none' || el.style?.visibility === 'hidden') return '';
      if (emitsBlock(el, tag)) return '\n';
      if (tag === 'IMG' || tag === 'AUDIO' || tag === 'VIDEO') return mediaSource(el, tag) ? '!' : '';
      if (emphasisTags.has(tag) ? !!el.textContent?.trim() : delimiterTags.has(tag) || tag === 'A' && !!el.getAttribute('href')) return '*';
      for (const child of Array.from(el.childNodes)) {
        const c = firstMarkdownChar(child, skip);
        if (c) return c;
      }
      return '';
    };
    const nextMarkdownChar = (node: Node, skip: Set<string>): string => {
      for (let n: Node | null = node; n; n = n.parentNode) {
        if (n !== node && (n.nodeType !== Node.ELEMENT_NODE || emitsBlock(n as Element, (n as Element).tagName.toUpperCase()))) return '';
        for (let s = n.nextSibling; s; s = s.nextSibling) {
          const c = firstMarkdownChar(s, skip);
          if (c) return c;
        }
      }
      return '';
    };
    const emphasisConverter = (marker: string, intrawordMarker = marker): PluginConverter => ({
      startTag(conversion, context): boolean {
        const { element } = conversion;
        const text = element.text() || '';
        if (!text.trim()) return true;
        if (/^\s/.test(text) && !conversion.atNoWhitespace) conversion.output(' ');
        const before = /^\s/.test(text) ? '' : ((conversion.last || '').match(/.$/su)?.[0] || '');
        const after = /\s$/.test(text) ? '' : nextMarkdownChar(element.node(), conversion.skipTagNames);
        const m = /[\p{L}\p{N}]/u.test(before + after) ? intrawordMarker : marker;
        context.set('marker', m);
        conversion.output(m);
        conversion.atNoWhitespace = true;
        return true;
      },
      endTag(conversion, context) {
        if (!context.has('marker')) return;
        const left = conversion.left.replace(/[ \t]+$/, '');
        let last = conversion.last || '';
        let trimmed = '';
        while (true) {
          const ws = last.match(/[ \t]+$/)?.[0] || (left && last.endsWith(left) ? left : '');
          if (!ws) break;
          trimmed += ws;
          last = last.substring(0, last.length - ws.length);
        }
        conversion.last = last;
        conversion.atNoWhitespace = false;
        conversion.output(context.get<string>('marker')!);
        if (trimmed) conversion.output(trimmed.includes('\n') ? '\n' : ' ');
      },
    });
    const emphasisProvider = (api: PluginApi): Plugin => {
      const bold = emphasisConverter('**');
      const italic = emphasisConverter('_', '*');
      return {
        converters: {
          B: bold,
          STRONG: bold,
          CITE: italic,
          DFN: italic,
          EM: italic,
          I: italic,
          U: italic,
          VAR: italic,
        },
      };
    };
    const preformattedProvider = (api: PluginApi): Plugin => ({
      converters: {
        PRE: {
          startTag(conversion): boolean {
            const code = conversion.element.text();
            const fence = '`'.repeat((code.match(/`+/g) || [])
              .reduce((length, run) => Math.max(length, run.length + 1), 3));
            conversion.appendParagraph().append(fence).append(conversion.left);
            conversion.output(code, { preserveLeadingWhitespace: true });
            if (!conversion.atLeft) conversion.append(conversion.left);
            conversion.atLeft = conversion.atParagraph = false;
            conversion.append(fence).appendParagraph();
            return false;
          },
        },
      },
    });
    const linkProvider = (api: PluginApi): Plugin => ({
      converters: {
        A: {
          startTag(conversion, context): boolean {
            const absolute = conversion.getOption('absolute');
            const inline = conversion.getOption('inline');
            const { element } = conversion;
            const href = element.attr('href');
            if (!href) {
              return true;
            }

            // Set inline flag
            // @ts-ignore
            conversion.inline = true;

            const title = element.attr('title');
            const url = absolute ? conversion.resolveUrl(href) : href;
            let value = title ? `${url} "${title}"` : url;

            if (inline) {
              value = `(${value})`;
            } else {
              const reference = conversion.addReference('link', value);
              value = `[${reference}]`;
            }

            context.set('value', value);
            conversion.output('[');
            conversion.atNoWhitespace = true;

            return true;
          },

          endTag(conversion, context) {
            if (context.has('value')) {
              conversion.output(`]${context.get<string>('value')}`);

              // Unset inline flag
              // @ts-ignore
              conversion.inline = false;
            }
          }
        }
      }
    });
    const audioProvider = (api: PluginApi): Plugin => ({
      startConversion(conversion) {
        conversion.skipTagNames.delete('AUDIO');
      },
      converters: {
        AUDIO: {
          startTag(conversion): boolean {
            const { element } = conversion;
            const source = element.find('source')?.attr('src') || element.attr('src');
            if (!source) {
              return false; // No source found, skip
            }

            const absolute = conversion.getOption('absolute');
            const url = absolute ? conversion.resolveUrl(source) : source;
            const value = `(${url})`;

            conversion.output(`![]${value}`);

            return false;
          },
        },
      }
    });
    const videoProvider = (api: PluginApi): Plugin => ({
      startConversion(conversion) {
        conversion.skipTagNames.delete('VIDEO');
      },
      converters: {
        VIDEO: {
          startTag(conversion): boolean {
            const { element } = conversion;
            const source = element.find('source')?.attr('src') || element.attr('src');
            if (!source) {
              return false; // No source found, skip
            }

            const absolute = conversion.getOption('absolute');
            const url = absolute ? conversion.resolveUrl(source) : source;
            const value = `(${url})`;

            conversion.output(`![]${value}`);

            return false;
          },
        },
      }
    });
    Europa.registerPlugin(superscriptProvider);
    Europa.registerPlugin(paragraphProvider);
    Europa.registerPlugin(emphasisProvider);
    Europa.registerPlugin(preformattedProvider);
    Europa.registerPlugin(linkProvider);
    Europa.registerPlugin(audioProvider);
    Europa.registerPlugin(videoProvider);
  }

  getUrlType(url: string) {
    if (url.startsWith(this.config.base)) {
      url = url.substring(this.config.base.length);
    }
    const basePath = getPath(this.config.base)!;
    if (url.startsWith(basePath)) {
      url = url.substring(basePath.length);
    }
    if (url.startsWith('/')) {
      url = url.substring(1);
    }
    return url.substring(0, url.indexOf('/'));
  }

  getRefUrl(url: string): string {
    if (url.startsWith('unsafe:')) url = url.substring('unsafe:'.length);
    const refPrefix = this.config.base + 'ref/';
    let ending = '';
    if (url.startsWith(refPrefix)) {
      ending = url.substring(refPrefix.length);
    } else {
      const relRefPrefix = getPath(refPrefix)!;
      if (url.startsWith(relRefPrefix)) {
        ending = url.substring(relRefPrefix.length);
      } else if (url.startsWith('/ref/')) {
        ending = url.substring('/ref/'.length);
      }
    }
    if (!ending) return url;
    if (ending.startsWith('/e/')){
      ending = ending.substring('/e/'.length);
      if (ending.indexOf('/') < 0) return decodeURIComponent(ending);
      return decodeURIComponent(ending.substring(0, ending.indexOf('/')));
    }
    return ending;
  }

  /**
   * Gets the query for a query URL.
   */
  getQuery(url: string): string {
    if (url.startsWith('unsafe:')) url = url.substring('unsafe:'.length);
    const tagPrefix = this.config.base + 'tag/';
    let ending = '';
    if (url.startsWith('tag:/')) {
      ending = url.substring('tag:/'.length);
    } else if (url.startsWith(tagPrefix)) {
      ending = url.substring(tagPrefix.length);
    } else {
      const relTagPrefix = getPath(tagPrefix)!;
      if (url.startsWith(relTagPrefix)) {
        ending = url.substring(relTagPrefix.length);
      } else if (url.startsWith('/tag/')) {
        ending = url.substring('/tag/'.length);
      }
    }
    if (!ending) return url;
    if (ending.indexOf('?') < 0) return ending;
    const query = ending.substring(0, ending.indexOf('?'))
    return decodeURIComponent(query);
  }

  /**
   * Add mailboxes for tagged users.
   */
  syncEditor(fb: UntypedFormBuilder, group: UntypedFormGroup, previousComment = '') {
    let comment = group.value.comment;
    // Store last synced comment in the form so that we can track what was already synced.
    // This will allow the user to remove a source, alt or tag without it being re-added
    // @ts-ignore
    previousComment ||= group.previousComment || '';
    // @ts-ignore
    group.previousComment = comment;
    this.syncMailboxes(fb, group, previousComment);
    group.get('comment')?.setValue(comment);
  }

  private syncMailboxes(fb: UntypedFormBuilder, group: UntypedFormGroup, previousComment = '') {
    const existingTags = [
      ...getMailboxes(previousComment, this.store.account.origin()),
      ...(group.value.tags || []),
    ];
    const mailboxes = uniq(difference([
      ...getMailboxes(group.value.comment, this.store.account.origin())], existingTags));
    for (const t of mailboxes) {
      (group.get('tags') as UntypedFormArray).push(fb.control(t, TagsFormComponent.validators));
    }
  }

  getTagPreview(tag: string, defaultOrigin = '', returnDefault = true, loadTemplates = true, loadPlugins = true): Observable<{ name?: string, tag: string } | undefined> {
    return this.exts.getCachedExt(tag, defaultOrigin).pipe(
      switchMap(x => {
        const localExists = x.modified && x.origin === (defaultOrigin || this.store.account.origin());
        if (loadTemplates) {
          const templates = this.admin.getTemplates(x.tag).filter(t => t.tag);
          if (templates.length) {
            const longestMatch = templates[templates.length - 1];
            if (!localExists) {
              if (x.tag === '+user') return of({ ...x, name: (longestMatch.config?.view || longestMatch.name || longestMatch.tag) + ' / ' + $localize`⚓️ Root` });
              if (x.tag === '_user') return of({ ...x, name: (longestMatch.config?.view || longestMatch.name || longestMatch.tag) + ' / ' + $localize`🥷 Root` });
            }
            if (x.tag === longestMatch.tag) return of(longestMatch);
            const childTag = removePrefix(x.tag, longestMatch.tag.split('/').length);
            return of({ tag: x.tag, name: (longestMatch.config?.view || longestMatch.name || longestMatch.tag) + ' / ' + (x.name || childTag) });
          }
        }
        if (localExists) return of(x);
        const plugin = this.admin.getPlugin(x.tag);
        if (loadPlugins) {
          if (plugin) return of(plugin);
          const parentPlugins = this.admin.getParentPlugins(x.tag);
          if (parentPlugins.length) {
            const longestMatch = parentPlugins[parentPlugins.length - 1];
            if (x.tag === longestMatch.tag) return of(longestMatch);
            const childTag = removePrefix(x.tag, longestMatch.tag.split('/').length);
            if (longestMatch.tag === 'plugin/outbox') {
              const origin = childTag.substring(0, childTag.indexOf('/'));
              const remoteTag = childTag.substring(origin.length + 1);
              const originFormat = origin ? ' @' + origin : '';
              return this.exts.getCachedExt(remoteTag, origin).pipe(
                map(c => ({ tag: x.tag, name: (longestMatch.name || longestMatch.tag) + ' / ' + (c.name || c.tag) + originFormat })),
              );
            }
            let a = access(x.tag) || '+';
            return this.exts.getCachedExt(a + setPublic(childTag), defaultOrigin).pipe(
              map(c => ({ tag: x.tag, name: (longestMatch.config?.view || longestMatch.name || longestMatch.tag) + ' / ' + (c.name || setPublic(childTag)) })),
            );
          }
        }
        if (x.modified || returnDefault) return of(x);
        return of(undefined);
      })
    );
  }

  getTagsPreview(tags: string[], defaultOrigin = ''): Observable<TagPreview[]> {
    return forkJoin(tags.map( t => this.getTagPreview(t, defaultOrigin))).pipe(
      map(xs => xs.filter(x => !!x)),
    );
  }

  getBookmarksPreview(bookmarks: string[], defaultOrigin = ''): Observable<TagPreview[]> {
    return forkJoin(bookmarks.map(b => {
      const query = b.includes('?') ? b.substring(0, b.indexOf('?')) : b;
      return this.getQueryPreviewName(query, defaultOrigin).pipe(
        map(name => ({ name, tag: query, bookmark: b })),
      );
    })).pipe(
      map(xs => xs.filter(x => !!x)),
    ) as Observable<TagPreview[]>;
  }

  /** Build a display name for a query by replacing each tag token with its ext preview name. */
  getQueryPreviewName(query: string, defaultOrigin = ''): Observable<string | undefined> {
    const tokens = query.split(/([:|()])/g).filter(t => !!t);
    const ops = new Set([':', '|', '(', ')']);
    const tagIndices = tokens.reduce<number[]>((acc, t, i) => ops.has(t) ? acc : [...acc, i], []);
    if (tagIndices.length === 0) return of(undefined);
    return forkJoin(tagIndices.map(i => {
      const token = tokens[i];
      const bareTag = token.startsWith('!') ? token.substring(1) : token;
      return this.getTagPreview(bareTag, defaultOrigin).pipe(
        map(x => {
          const name = x?.name || bareTag;
          return token.startsWith('!') ? '!' + name : name;
        }),
      );
    })).pipe(
      map(names => {
        let idx = 0;
        return tokens.map(t => ops.has(t) ? t : names[idx++]).join('');
      }),
    );
  }
}
